import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import { Box, Typography } from '@mui/material';
import type { MembershipSearchItem } from '@shared/dto/auth/membership-search.response.dto';
import { useTranslation } from 'react-i18next';
import { visuallyHidden } from '../../utils/visually-hidden';
import { TenantStatusChip } from '../tenant-status-chip';
import { CompanyLogoAvatar } from './company-logo-avatar';
import { WorkspaceRelationChip } from './workspace-relation-chip';

interface CompanyOptionContentProps {
  membership: MembershipSearchItem;
  isCurrent: boolean;
}

export function CompanyOptionContent({ membership, isCurrent }: CompanyOptionContentProps) {
  const { t } = useTranslation();
  // Sulla propria azienda/studio lo stato si mostra solo quando c'è un problema da vedere
  // (scaduto, pagamento in ritardo, annullato): un partner non è mai "in prova".
  const showStatusChip =
    !membership.workspaceRelation ||
    (membership.subscriptionStatus !== 'ACTIVE' && membership.subscriptionStatus !== 'TRIALING');
  const secondaryLine = [
    membership.ragioneSociale && membership.ragioneSociale !== membership.tenantName
      ? membership.ragioneSociale
      : null,
    formatLocation(membership.indirizzoCitta, membership.indirizzoProvincia),
    membership.partitaIva
      ? t('consultant.companyPicker.vatLabel', { vat: membership.partitaIva })
      : null,
  ]
    .filter((part): part is string => Boolean(part))
    .join(' · ');

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', minWidth: 0 }}>
      <CompanyLogoAvatar logoUrl={membership.logoUrl} size={32} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          title={membership.tenantName}
          sx={{
            fontWeight: 700,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            overflowWrap: 'anywhere',
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: { xs: 2, md: 1 },
          }}
        >
          {membership.tenantName}
          {isCurrent && (
            <Box component="span" sx={visuallyHidden}>
              {` (${t('consultant.currentCompany')})`}
            </Box>
          )}
        </Typography>
        {secondaryLine && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'text.secondary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {secondaryLine}
          </Typography>
        )}
      </Box>
      {membership.workspaceRelation && (
        <WorkspaceRelationChip workspaceRelation={membership.workspaceRelation} />
      )}
      {showStatusChip && (
        <TenantStatusChip
          subscriptionStatus={membership.subscriptionStatus}
          isManaged={membership.isManaged}
          isManagedByMe={membership.isManagedByMe}
          managedBy={membership.managedBy}
        />
      )}
      {isCurrent && (
        <CheckOutlinedIcon
          aria-hidden
          sx={{ fontSize: 18, color: 'primary.main', flexShrink: 0 }}
        />
      )}
    </Box>
  );
}

function formatLocation(city: string | null, province: string | null): string | null {
  if (city && province) {
    return `${city} (${province})`;
  }
  return city ?? province;
}
